<template>
  <v-card elevation="4">
    <v-card-title class="d-flex justify-space-between">
      <span>{{ $tc('temperature_violation', 2)}}</span>
      <info-modal width="800">
        <v-card-title>
          <h4>{{ $tc('temperature_violation', 2) }}</h4>
        </v-card-title>
        <v-card-text v-html="$t('fleet_health_ui.modal_info.temperature_violations')"/>
      </info-modal>
    </v-card-title>
    <v-card-text v-if="!loading">
      <div v-if="combinedVehicles.length > 0">
        <div class="text-subtitle-2 ml-4">{{ $t('fleet_health_ui.last_30_days') }}</div>
        <div class="d-flex justify-space-around mt-2">
          <v-btn text color="temperature-hot" class="violating-routes" large
                 :to="violatingRoutesLink('hot')"
                 :disabled="totalHotViolations === 0">
            <v-icon color="temperature-hot" size="40">mdi-fire</v-icon>
            <span>{{ totalHotViolations }}</span>
          </v-btn>
          <v-btn text color="temperature-cold" class="violating-routes" large
                 :to="violatingRoutesLink('cold')"
                 :disabled="totalColdViolations === 0">
            <v-icon color="temperature-cold" size="40">mdi-snowflake</v-icon>
            <span>{{ totalColdViolations }}</span>
          </v-btn>
        </div>
        <v-list subheader>
          <v-subheader>{{ $t('fleet_health_ui.top_3_most_frequent_violators') }}</v-subheader>
          <v-list-item-group>
            <v-list-item
              dense
              v-for="(vehicle, index) in topViolators" :key="index"
              :to="`/vehicle/${vehicle.id}`"
              class="primary--text">
              <v-list-item-icon>
                <v-icon color="primary" class="mt-1">mdi-truck-alert</v-icon>
              </v-list-item-icon>
              <v-list-item-content class="ml-n4">
                {{ vehicle.name }}
              </v-list-item-content>
              <v-list-item-action class="d-flex flex-row">
                <v-btn
                  class="small-temperature-button"
                  x-small
                  text
                  :to="violatingRoutesLink('hot', vehicle.id)" color="temperature-hot"
                  :disabled="violationCount(vehicle, 'hot') === 0">
                  <v-icon>mdi-fire</v-icon>{{ violationCount(vehicle, 'hot') }}
                </v-btn>
                <v-btn
                  class="small-temperature-button"
                  x-small
                  text
                  :to="violatingRoutesLink('cold', vehicle.id)" color="temperature-cold"
                  :disabled="violationCount(vehicle, 'cold') === 0">
                  <v-icon>mdi-snowflake</v-icon>{{ violationCount(vehicle, 'cold') }}
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </div>
      <div v-else id="status-green" class="status-green text-center">
        <div class="text-subtitle-1">{{ $t('no_violations') }}</div>
        <v-icon color="success" size="50">mdi-check-bold</v-icon>
      </div>
    </v-card-text>
    <v-card-text v-else>
      <v-skeleton-loader type="list-item" width="50%"/>
      <div class="d-flex justify-space-around my-1">
        <v-skeleton-loader type="avatar"/>
        <v-skeleton-loader type="avatar"/>
      </div>
      <v-skeleton-loader dense type="list-item-avatar" v-for="i in 2" :key="i"/>
    </v-card-text>
  </v-card>
</template>

<script>
import InfoModal from '@/components/shared/InfoModal'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import Moment from 'moment-timezone'
import { sumBy, each, take, concat, map, get, values, clone } from 'lodash'

export default {
  name: 'TemperatureViolations',
  components: {
    InfoModal
  },
  mixins: [
    CurrentUserMixin
  ],
  props: {
    showAllCustomers: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      violationsData: [],
      loading: true
    }
  },
  mounted () {
    this.fetchData()
  },
  watch: {
    showAllCustomers () {
      this.fetchData()
    }
  },
  methods: {
    /**
     * Retrieve the data from the API.
     */
    async fetchData () {
      try {
        this.loading = true
        const customerIds = this.showAllCustomers ? (map(this.regularCustomers, x => x.id)).join(',') : this.customerContext.id
        const resp = await this.$httpAdapter.get('/customer-insights/violations', {
          params: {
            customer_id: customerIds
          }
        })
        this.violationsData = resp.data
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loading = false
      }
    },

    /**
     * Returns the URL for the route index of the routes with the specified violation
     *
     * @param violationType (e.g. hot, cold)
     * @param vehicleId
     */
    violatingRoutesLink (violationType, vehicleId) {
      const startDate = Moment().tz(this.customerContext.timezone()).subtract(30, 'day').format('YYYY-MM-DD HH:mm:ss')
      const endDate = Moment().tz(this.customerContext.timezone()).format('YYYY-MM-DD HH:mm:ss')

      let link = `/route/index?startDate=${startDate}&endDate=${endDate}&violations[]=${violationType}&excludeProvisional=1`

      if (vehicleId) {
        link += `&vehicleId=${vehicleId}`
      }

      return link
    },

    /**
     * The number of violations of the given type recorded against a vehicle.
     * Each entry is a violating route carrying the number of violations on that route, so the entries
     * have to be summed rather than counted. Entries archived before violation_count was recorded only
     * ever represented a single violation each.
     *
     * @param vehicle
     * @param type (e.g. hot, cold)
     * @returns int
     */
    violationCount (vehicle, type) {
      return sumBy(get(vehicle, type, []), violatingRoute => get(violatingRoute, 'violation_count', 1))
    },

    /**
     * Refresh the page when the customer context changes.
     */
    customerContextChangeHandler () {
      this.dataLoaded = false
      this.fetchData()
    }
  },
  computed: {
    /**
     * Compile the vehicles from all the customers into one flattened collection.
     * If the vehicle exists in more than one customer, combine the violations.
     * This way, we don't show a vehicle multiple times for two different customers.
     * It doesn't make sense for every Insights Dashboard widget, but I think is applicable here.
     */
    combinedVehicles () {
      const resultMap = {}
      each(this.violationsData, customerData => {
        each(customerData, vehicle => {
          const existingVehicle = get(resultMap, vehicle.id)
          if (existingVehicle) {
            existingVehicle.hot = concat(existingVehicle.hot, vehicle.hot)
            existingVehicle.cold = concat(existingVehicle.cold, vehicle.cold)
          } else {
            // Clone so that combining a vehicle's violations does not mutate the fetched response.
            resultMap[vehicle.id] = clone(vehicle)
          }
        })
      })
      return values(resultMap)
    },
    /**
     * The total number of hot violations across all routes and customers
     * @returns int
     */
    totalHotViolations () {
      return sumBy(this.combinedVehicles, vehicle => {
        return this.violationCount(vehicle, 'hot')
      })
    },
    /**
     * The total number of cold violations across all routes and customers
     * @returns int
     */
    totalColdViolations () {
      return sumBy(this.combinedVehicles, vehicle => {
        return this.violationCount(vehicle, 'cold')
      })
    },
    /**
     * The top violators across all customers.
     * Sort by number of violations and take the top 3.
     * @returns Array
     */
    topViolators () {
      const sorted = clone(this.combinedVehicles).sort((first, second) => {
        const firstTotal = this.violationCount(first, 'hot') + this.violationCount(first, 'cold')
        const secondTotal = this.violationCount(second, 'hot') + this.violationCount(second, 'cold')
        if (firstTotal < secondTotal) {
          return 1
        }
        if (firstTotal > secondTotal) {
          return -1
        }
        return 0
      })
      return take(sorted, 3)
    }
  }
}
</script>

<style scoped lang="scss">
.v-btn.violating-routes {
  font-family: "Inter Bold", sans-serif;
  font-size: 35px;
}

.v-btn.small-temperature-button {
  justify-content: start;
  min-width: 70px;
}

</style>
